/**
 * `useInsights` — fetch system-surfaced graph insights for a case.
 *
 * Wraps the existing Insights Dashboard endpoints (`getComponents`,
 * `getBridges`, `getTopEntities`) into a unified ranked `Insight[]`. The
 * strip on the Graph page renders as many of these as fit in a single row
 * (driven by the container width), so we return more than the visible
 * minimum so wider screens have material to show. Mixes three kinds:
 * clusters, Person top entities, cross-cluster bridges, with per-kind caps
 * and overflow-backfill (clusters → bridges → persons priority).
 *
 * `refresh()` runs `computeAnalysis` server-side and refetches.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type BridgeInsight,
  type ComponentsResponse,
  graphApi,
  type TopEntitiesResponse,
} from '@/services/api/graph'
import type { Insight } from './types'

// Total strip cap. Larger than the visible minimum so wide displays can
// surface more cards; the strip slices to whatever fits in one row.
const STRIP_LIMIT = 12
const PER_KIND_LIMIT = 4

interface InsightsBundle {
  components: ComponentsResponse
  bridges: BridgeInsight[]
  topEntities: TopEntitiesResponse
}

function buildClusters(bundle: InsightsBundle, limit: number): Insight[] {
  return bundle.components.components
    .slice()
    .sort((a, b) => b.size - a.size)
    .slice(0, limit)
    .map<Insight>(c => ({
      id: `cluster-${c.comp_id}`,
      kind: 'cluster',
      title: `Cluster ${c.comp_id}`,
      summary:
        c.entity_types.length > 0
          ? `${c.entity_types.slice(0, 3).join(', ')}${c.entity_types.length > 3 ? '…' : ''}`
          : 'Mixed entity types',
      metadata: {
        kind: 'cluster',
        componentId: c.comp_id,
        entityCount: c.size,
        relationshipCount: c.relationship_count,
      },
      rank: 0,
    }))
}

function buildPersons(bundle: InsightsBundle, limit: number): Insight[] {
  const personGroup = bundle.topEntities.entity_types.find(g => g.entity_type === 'Person')
  if (!personGroup) return []
  return personGroup.top_entities.slice(0, limit).map<Insight>(e => ({
    id: `person-${e.id}`,
    kind: 'top_entity',
    title: e.name,
    summary: 'Person',
    metadata: {
      kind: 'top_entity',
      entityId: e.id,
      entityName: e.name,
      entityType: 'Person',
      score: e.score,
      degree: e.degree,
      betweenness: e.betweenness,
      sourceCount: e.source_count,
    },
    rank: 0,
  }))
}

function buildBridges(bundle: InsightsBundle, limit: number): Insight[] {
  return bundle.bridges.slice(0, limit).map<Insight>((b, i) => ({
    id: `bridge-${b.entity_a_id}-${b.entity_b_id}`,
    kind: 'bridge',
    title: `Cluster ${b.component_a} → Cluster ${b.component_b}`,
    summary: `${b.entity_a_name} ↔ ${b.entity_b_name}`,
    metadata: {
      kind: 'bridge',
      entityAId: b.entity_a_id,
      entityAName: b.entity_a_name,
      entityAType: b.entity_a_type,
      componentA: b.component_a,
      entityBId: b.entity_b_id,
      entityBName: b.entity_b_name,
      entityBType: b.entity_b_type,
      componentB: b.component_b,
      documentTitle: b.document_title,
      documentId: b.document_id,
    },
    rank: i,
  }))
}

export function rankAndPickTopInsights(bundle: InsightsBundle): Insight[] {
  // First pass: top N of each kind.
  const clusters = buildClusters(bundle, PER_KIND_LIMIT)
  const persons = buildPersons(bundle, PER_KIND_LIMIT)
  const bridges = buildBridges(bundle, PER_KIND_LIMIT)

  let mixed = [...clusters, ...persons, ...bridges]

  // Backfill if we have fewer than STRIP_LIMIT — pull more clusters first
  // (most informative), then bridges, then persons.
  if (mixed.length < STRIP_LIMIT) {
    const extraClusters = buildClusters(bundle, STRIP_LIMIT).slice(clusters.length)
    const extraBridges = buildBridges(bundle, STRIP_LIMIT).slice(bridges.length)
    const extraPersons = buildPersons(bundle, STRIP_LIMIT).slice(persons.length)
    mixed = [...mixed, ...extraClusters, ...extraBridges, ...extraPersons]
  }

  // Assign ranks by final position so the UI can use it for display ordering.
  return mixed.slice(0, STRIP_LIMIT).map((insight, i) => ({ ...insight, rank: i }))
}

export function useInsights(caseId: string) {
  const queryClient = useQueryClient()
  const queryKey = ['insights', caseId] as const

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<Insight[]> => {
      const [components, bridgeRes, topEntities] = await Promise.all([
        graphApi.getComponents(caseId),
        graphApi.getBridges(caseId),
        graphApi.getTopEntities(caseId),
      ])
      return rankAndPickTopInsights({
        components,
        bridges: bridgeRes.bridges,
        topEntities,
      })
    },
  })

  const refresh = async () => {
    await graphApi.computeAnalysis(caseId)
    await queryClient.invalidateQueries({ queryKey })
    return query.refetch()
  }

  return {
    insights: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refresh,
  }
}
